"use client";

import { getTranslation } from "@/i18n/get-translation";

import {
  ChangeEvent,
  Dispatch,
  FormEvent,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  IAppointmentPayload,
  IBookingPatientData,
  IFormData,
} from "@/types/agenda.type";
import { initialFormData } from "@/constants/agenda";

interface IDoctorAppointmetDetails {
  locale: string;
  setAppointmentPayload: Dispatch<
    SetStateAction<IAppointmentPayload | undefined>
  >;
  isLoading?: boolean;
}

export default function DoctorAppointmetDetails({
  locale,
  setAppointmentPayload,
  isLoading,
}: IDoctorAppointmetDetails) {
  const t = getTranslation(locale, "appointment");

  const [first_name, setFirstName] = useState<string>("");
  const [last_name, setLastName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [matricule_code, setMatriculeCode] = useState<string>("");
  const [gender, setGender] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [dob, setDOB] = useState<string>("");
  const [phone, setPhone] = useState<string>("");

  const [errors, setErrors] = useState<IFormData>(initialFormData);
  const [isFormValid, setIsFormValid] = useState<boolean>(true);
  const [isSubmit, setIsSubmit] = useState<boolean>(false);

  useEffect(() => {
    validateForm();
  }, [
    matricule_code,
    gender,
    first_name,
    last_name,
    email,
    phone,
    dob,
    message,
  ]);

  useEffect(() => {
    if (isSubmit) {
      validateForm();
    }
  }, [isSubmit]);

  const validateForm = useCallback(() => {
    if (!isFormValid) {
      let errObj: any = {};

      if (!first_name) {
        errObj.first_name = t("error_msgs.first_name");
      }

      if (!last_name) {
        errObj.last_name = t("error_msgs.last_name");
      }

      if (!email) {
        errObj.email = t("error_msgs.email_req");
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        errObj.email = t("error_msgs.email_invalid");
      }

      if (!gender) {
        errObj.gender = t("error_msgs.gender");
      }

      if (!dob) {
        errObj.dob = t("error_msgs.dob");
      }

      if (!phone) {
        errObj.gsm_phone_number = t("error_msgs.phone");
      }

      if (!matricule_code) {
        errObj.matricule_code = t("error_msgs.matricule_code");
      }

      if (!message) {
        errObj.message = t("error_msgs.message");
      }

      setErrors(errObj);
      setIsFormValid(Object.keys(errObj).length === 0);
    }
  }, [errors, isFormValid]);

  const handleFormSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      setIsSubmit(true);

      if (!isFormValid) return;

      // LUCY-3110: no more patient matching. We always submit the booking
      // with patient_id null and the full form data as patient_data. The
      // backend (LUCY-3010, already on staging) creates the appointment
      // with no patient row, attaches a Doctena-format comment for the
      // secretary, and notifies via the appointment (which routes the
      // confirmation email via patient_data.email).
      const patient_data: IBookingPatientData = {
        first_name,
        last_name,
        email,
        phone,
        dob,
        matricule_code,
      };

      setAppointmentPayload((prev) => ({
        ...prev,
        patient_id: null,
        comment: message,
        patient_data,
      }));
    },
    [
      isFormValid,
      first_name,
      last_name,
      email,
      phone,
      dob,
      matricule_code,
      message,
    ]
  );

  const currentDate = useMemo(() => {
    return new Date().toISOString().split("T")[0];
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <div className="relative">
      <div>
        <div
          className="flex flex-wrap text-left -mx-2"
        >
          <div className="p-2 w-full">
            <div className="relative">
              <label
                htmlFor="matricule_code"
                className="leading-7 pl-4 text-agenda-purple-800 opacity-80 mb-1 block text-sm md:text-base"
              >
                {t("matricule_number")}
              </label>
              <input
                type="text"
                id="matricule_code"
                name="matricule_code"
                maxLength={20}
                placeholder="1234567890"
                className={`w-full bg-white rounded-full border border-agenda-grey-200 focus:border-agenda-blue-600 outline-none text-agenda-purple-800 py-1 md:py-2 px-4 leading-8 transition-colors duration-200 ease-in-out ${
                  errors.matricule_code && isSubmit && "border-red-400"
                } text-sm md:text-base`}
                value={matricule_code}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setMatriculeCode(e.target.value)
                }
                onKeyDown={handleKeyDown}
              />
              {errors.matricule_code && isSubmit && (
                <div className="text-red-600 pl-4 mt-1">
                  {errors.matricule_code}
                </div>
              )}
            </div>
          </div>
          <div className="p-2 lg:w-1/2 w-full">
            <div className="relative">
              <label
                htmlFor="fname"
                className="leading-7 pl-4 text-agenda-purple-800 opacity-80 mb-1 block text-sm md:text-base"
              >
                {t("first_name")}
              </label>
              <input
                type="text"
                id="fname"
                name="fname"
                maxLength={100}
                placeholder="Max"
                className={`w-full bg-white rounded-full border border-agenda-grey-200 focus:border-agenda-blue-600 outline-none text-agenda-purple-800 py-1 md:py-2 px-4 leading-8 transition-colors duration-200 ease-in-out ${
                  errors.first_name && isSubmit && "border-red-400"
                } text-sm md:text-base`}
                value={first_name}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setFirstName(e.target.value)
                }
              />
              {errors.first_name && isSubmit && (
                <div className="text-red-600 pl-4 mt-1">
                  {errors.first_name}
                </div>
              )}
            </div>
          </div>
          <div className="p-2 lg:w-1/2 w-full">
            <div className="relative">
              <label
                htmlFor="lname"
                className="leading-7 pl-4 text-agenda-purple-800 opacity-80 mb-1 block text-sm md:text-base"
              >
                {t("last_name")}
              </label>
              <input
                type="text"
                id="lname"
                name="lname"
                maxLength={100}
                placeholder="Mustermann"
                className={`w-full bg-white rounded-full border border-agenda-grey-200 focus:border-agenda-blue-600 outline-none text-agenda-purple-800 py-1 md:py-2 px-4 leading-8 transition-colors duration-200 ease-in-out ${
                  errors.last_name && isSubmit && "border-red-400"
                } text-sm md:text-base`}
                value={last_name}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setLastName(e.target.value)
                }
              />
              {errors.last_name && isSubmit && (
                <div className="text-red-600 pl-4 mt-1">
                  {errors.last_name}
                </div>
              )}
            </div>
          </div>
          <div className="p-2 lg:w-1/2 w-full">
            <div className="relative">
              <label
                htmlFor="Email"
                className="leading-7 pl-4 text-agenda-purple-800 opacity-80 mb-1 block text-sm md:text-base"
              >
                {t("email")}
              </label>
              <input
                type="email"
                id="Email"
                name="Email"
                maxLength={254}
                placeholder="mustermann@medlucy.com"
                className={`w-full bg-white rounded-full border border-agenda-grey-200 focus:border-agenda-blue-600 outline-none text-agenda-purple-800 py-1 md:py-2 px-4 leading-8 transition-colors duration-200 ease-in-out ${
                  errors.email && isSubmit && "border-red-400"
                } text-sm md:text-base`}
                value={email}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setEmail(e.target.value)
                }
              />
              {errors.email && isSubmit && (
                <div className="text-red-600 pl-4 mt-1">
                  {errors.email}
                </div>
              )}
            </div>
          </div>
          <div className="p-2 lg:w-1/2 w-full">
            <div className="relative">
              <label
                htmlFor="phone"
                className="leading-7 pl-4 text-agenda-purple-800 opacity-80 mb-1 block text-sm md:text-base"
              >
                {t("phone")}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                maxLength={20}
                placeholder="+352 11 11 11 11"
                className={`w-full bg-white rounded-full border border-agenda-grey-200 focus:border-agenda-blue-600 outline-none text-agenda-purple-800 py-1 md:py-2 px-4 leading-8 transition-colors duration-200 ease-in-out ${
                  errors.gsm_phone_number && isSubmit && "border-red-400"
                } text-sm md:text-base`}
                value={phone}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setPhone(e.target.value)
                }
              />
              {errors.gsm_phone_number && isSubmit && (
                <div className="text-red-600 pl-4 mt-1">
                  {errors.gsm_phone_number}
                </div>
              )}
            </div>
          </div>
          <div className="p-2 lg:w-1/2 w-full">
            <div className="relative">
              <label
                htmlFor="Discipline"
                className="leading-7 pl-4 text-agenda-purple-800 opacity-80 mb-1 block text-sm md:text-base"
              >
                {t("gender")}
              </label>
              <select
                id="gender"
                name="gender"
                className={`w-full bg-white rounded-full border border-agenda-grey-200 focus:border-agenda-blue-600 outline-none text-agenda-purple-800 py-1 md:py-2 px-4 leading-8 transition-colors duration-200 ease-in-out appearance-none bg-[url('/icons/angleDown.svg')] bg-no-repeat bg-[length:0.8rem] bg-[right_1rem_center] ${
                  errors.gender && isSubmit && "border-red-400"
                } text-sm md:text-base`}
                value={gender}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  setGender(e.target.value)
                }
              >
                <option value="">{t("select")}</option>
                <option value="male">{t("male")}</option>
                <option value="female">{t("female")}</option>
                <option value="other">{t("other")}</option>
              </select>
              {errors.gender && isSubmit && (
                <div className="text-red-600 pl-4 mt-1">
                  {errors.gender}
                </div>
              )}
            </div>
          </div>
          <div className="p-2 lg:w-1/2 w-full">
            <div className="relative">
              <label
                htmlFor="dob"
                className="leading-7 pl-4 text-agenda-purple-800 opacity-80 mb-1 block text-sm md:text-base"
              >
                {t("dob")}
              </label>
              <input
                type="date"
                id="dob"
                name="dob"
                className={`w-full bg-white rounded-full border border-agenda-grey-200 focus:border-agenda-blue-600 outline-none text-agenda-purple-800 py-2 px-4 leading-8 transition-colors duration-200 ease-in-out ${
                  errors.dob && isSubmit && "border-red-400"
                } text-sm md:text-base`}
                value={dob}
                max={currentDate}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setDOB(e.target.value)
                }
              />
              {errors.dob && isSubmit && (
                <div className="text-red-600 pl-4 mt-1">
                  {errors.dob}
                </div>
              )}
            </div>
          </div>
          <div className="p-2 w-full">
            <div className="relative">
              <label
                htmlFor="message"
                className="leading-7 pl-4 text-agenda-purple-800 opacity-80 mb-1 block text-sm md:text-base"
              >
                {t("message")}
              </label>
              <textarea
                id="message"
                name="message"
                maxLength={1000}
                placeholder={t("placeholders.message")}
                className={`w-full bg-white rounded-2xl border border-agenda-grey-200 focus:border-agenda-blue-600 h-32 outline-none text-agenda-purple-800 py-2 px-4 resize-none leading-6 transition-colors duration-200 ease-in-out text-sm md:text-base ${
                  errors.message && isSubmit && "border-red-400"
                }`}
                value={message}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                  setMessage(e.target.value)
                }
              ></textarea>
              {errors.message && isSubmit && (
                <div className="text-red-600 pl-4 mt-1">
                  {errors.message}
                </div>
              )}
            </div>
          </div>
          <div className="p-2 w-full">
            <button
              type="submit"
              className="whitespace-nowrap text-white bg-agenda-blue-600 hover:bg-agenda-blue-700 active:scale-95 h-12 rounded-lg py-3 px-8 leading-4 flex items-center justify-center font-bold disabled:bg-gray-400 disabled:cursor-not-allowed disabled:active:scale-100"
              onClick={handleFormSubmit}
              disabled={isLoading}
            >
              {t("submit")}
            </button>
          </div>
        </div>
      </div>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/70">
          <div className="loader"></div>
        </div>
      )}
    </div>
  );
}
